import { Link } from "react-router-dom";
import { useReveal } from "../lib/useReveal";
import { Newspaper, ExternalLink, Quote } from "lucide-react";
import FieldVisitNews from "./FieldVisitNews";

const WELL_WISHERS = [
  {
    quote: "Akshara Daana treats rural children with genuine respect — not as recipients, but as protagonists of their own learning. It is a rare, values-led initiative worth every bit of its ambition.",
    name: "Prof. Dr. Arcot Purna Prasad",
    role: "Director, CMR University",
    initials: "AP",
    accent: "bg-teal",
  },
  {
    quote: "What Karnataka Kalashree Smt. Revathi Kamath has built here is rare — a patient, transparent programme that will outlast every trend. I stand with this work wholeheartedly.",
    name: "Dr. Balbir Singh Bedi",
    role: "Homoeopathy Physician & Writer",
    initials: "BB",
    accent: "bg-coral",
  },
  {
    quote: "Akshara Daana is not charity — it is a partnership with rural schools, teachers and children. Its long view of what a village school can become is exactly what India needs more of.",
    name: "Dr. Smrite Goudhaman",
    role: "Adjunct Professor, Golden Gate University",
    initials: "SG",
    accent: "bg-navy",
  },
];

const NEWS = [
  { date: "Sep 2026", title: "Work begins at Hallagere school; committee visits Besagarahalli & Bidarakote and reports on infrastructure needs", link: "/news#mandya-visit" },
  { date: "Aug 31, 2026", title: "Field visit to Lakshmeshwar, Gadag — team inspects Government Model Primary School and meets students", link: "/news#lakshmeshwar-visit" },
  { date: "Jul 2026", title: "Akshara Daana opens its Gadag chapter — the flag rises over a town that has taught India for six centuries", link: "/causes#gadag" },
  { date: "Nov 20, 2025", title: "Rural school restoration at Naganayakanahalli — a case study in patient work", link: "#" },
  { date: "Oct 12, 2025", title: "Karnataka Kalashree honour recognises decades of arts and social work by Smt. Revathi Kamath", link: "#" },
  { date: "Sep 04, 2025", title: "How Akshara Daana adopts a school — a step-by-step field note", link: "#" },
  { date: "Aug 15, 2025", title: "Gadag chapter announcement — Akshara Daana to open its next district in 2026", link: "#" },
  { date: "Jun 02, 2025", title: "Halegere School reopens: from broken sanitation to a full restoration", link: "#" },
];

const LOGOS = [
  "AarogyaSeva Foundation",
  "Karnataka Education Dept.",
  "Rotary Club Placeholder",
  "Local School Consortium",
  "Gadag District Admin.",
  "Panchakshari Trust",
  "Kannada Sahitya Parishat",
  "IIM Alumni Circle",
];

export default function Media() {
  const ref = useReveal();
  const ref2 = useReveal();

  return (
    <>
      {/* Well-wishers */}
      <section id="voices" data-testid="voices-section" className="relative py-16 md:py-28 bg-parchment-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={ref} className="reveal max-w-3xl text-center md:text-left">
            <span className="section-chip">Testimonials</span>
            <h2 className="mt-3 font-display font-extrabold text-navy text-3xl md:text-5xl leading-[1.1] tracking-tight">
              Voices of our <em>Well-Wishers.</em>
            </h2>
            <p className="mt-3 text-indigo/70 font-display">Hear from the people who make our work possible.</p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {WELL_WISHERS.map((w, i) => (
              <div key={i} data-testid={`wellwisher-${i}`} className="manuscript-card group min-w-0">
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-full ${w.accent} text-parchment flex items-center justify-center font-yatra text-lg shrink-0 border-2 border-turmeric`}>
                    {w.initials}
                  </div>
                  <div className="min-w-0">
                    <div className="font-yatra text-indigo text-base leading-tight">{w.name}</div>
                    <div className="text-xs uppercase tracking-widest text-indigo/60 font-display mt-0.5">{w.role}</div>
                  </div>
                </div>
                <Quote className="w-5 h-5 text-turmeric mb-1" />
                <blockquote className="quote text-indigo text-lg leading-snug">&ldquo;{w.quote}&rdquo;</blockquote>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* News + Supporters */}
      <section id="media" data-testid="media-section" className="relative py-16 md:py-28 bg-parchment overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Featured — latest field visit */}
          <div className="mb-14 md:mb-20">
            <FieldVisitNews />
          </div>

          <div ref={ref2} className="reveal grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2 min-w-0">
              <div className="flex items-center gap-2 mb-4">
                <Newspaper className="w-5 h-5 text-vermilion" />
                <span className="section-chip">Media</span>
              </div>
              <h2 className="font-display font-extrabold text-navy text-3xl md:text-4xl leading-[1.1] tracking-tight">
                Akshara Daana in the <em>News.</em>
              </h2>
              <ul className="mt-8 space-y-4">
                {NEWS.map((n, i) => {
                  const Tag = n.link.startsWith("/") ? Link : "a";
                  const linkProps = n.link.startsWith("/") ? { to: n.link } : { href: n.link };
                  return (
                  <li key={i} data-testid={`news-${i}`} className="dashed-divider pt-4 first:border-t-0 first:pt-0">
                    <Tag {...linkProps} className="group flex items-start gap-3">
                      <span className="text-turmeric font-display text-xs uppercase tracking-widest w-20 shrink-0 mt-1">{n.date}</span>
                      <span className="font-display text-indigo text-sm md:text-base group-hover:text-vermilion transition-colors flex-1 min-w-0">
                        {n.title}
                      </span>
                      <ExternalLink className="w-4 h-4 text-indigo/40 group-hover:text-vermilion mt-1 shrink-0" />
                    </Tag>
                  </li>
                  );
                })}
              </ul>
              <button className="mt-6 btn-outline-indigo text-sm">View All</button>
            </div>

            <div className="lg:col-span-3 min-w-0 overflow-hidden">
              <span className="section-chip">Backed By</span>
              <h2 className="mt-3 font-display font-extrabold text-navy text-3xl md:text-4xl leading-[1.1] tracking-tight">
                Our <em>Supporters.</em>
              </h2>
              <p className="mt-2 text-indigo/70 font-display text-sm">
                Institutional partners, community trusts and long-standing well-wishers who stand with us.
              </p>
              <div className="mt-6 relative overflow-hidden w-full max-w-full py-4 border-y border-dashed border-indigo/30">
                <div className="marquee-track gap-14 items-center">
                  {[...LOGOS, ...LOGOS].map((name, i) => (
                    <div
                      key={i}
                      className="shrink-0 px-5 py-3 border border-indigo/25 bg-parchment-light text-indigo/80 font-yatra text-sm whitespace-nowrap"
                    >
                      {name}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
